/**
 * NewRecordDefaultsStatus — stan formularza nowego rekordu, zanim wartości domyślne pól są
 * wczytane (`useNewRecordDefaults`): kręciołek w trakcie, komunikat z „Spróbuj ponownie” przy
 * błędzie. Strona renderuje go ZAMIAST pól, dopóki `status` nie jest `ready` — formularz nie
 * otwiera się po cichu bez wartości.
 */

import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/Alert';
import { Button } from '@/components/Button';
import { Spinner } from '@/components/Spinner';
import type { NewRecordDefaults } from '@/hooks/useNewRecordDefaults';

export interface NewRecordDefaultsStatusProps {
  defaults: Pick<NewRecordDefaults, 'status' | 'retry'>;
  /** Prefiks data-testid strony (np. `pi-detail`). */
  testIdPrefix: string;
}

export function NewRecordDefaultsStatus({ defaults, testIdPrefix }: NewRecordDefaultsStatusProps) {
  const { t } = useTranslation();

  if (defaults.status === 'error') {
    return (
      <div className="p-6">
        <Alert
          tone="error"
          data-testid={`${testIdPrefix}-defaults-error`}
          actions={
            <Button
              size="sm"
              variant="secondary"
              onClick={defaults.retry}
              data-testid={`${testIdPrefix}-defaults-retry`}
            >
              {t('common.recordDefaults.retry')}
            </Button>
          }
        >
          {t('common.recordDefaults.loadError')}
        </Alert>
      </div>
    );
  }

  return (
    <div
      className="flex items-center justify-center p-12"
      data-testid={`${testIdPrefix}-defaults-loading`}
    >
      <Spinner />
    </div>
  );
}
